import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

// Animation Variants
const fadeInUp = {
  hidden: { 
    opacity: 0, 
    y: 60,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

const imageVariants = {
  hidden: { 
    opacity: 0, 
    y: 40,
    scale: 0.9
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const slideInLeft = {
  hidden: { 
    opacity: 0, 
    x: -80,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const slideInRight = {
  hidden: { 
    opacity: 0, 
    x: 80,
    scale: 0.95
  },
  visible: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Styled Components
const CompanyContainer = styled.section`
  background-color: #ffffff;
  padding: 80px 0;
  margin: 0;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 3rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const ContentSection = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 80px;
  gap: 60px;

  @media (max-width: 1024px) {
    gap: 40px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
    margin-bottom: 60px;
  }

  @media (max-width: 480px) {
    gap: 20px;
    margin-bottom: 40px;
  }

  &:nth-child(even) {
    flex-direction: row-reverse;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }
`;

const TextContent = styled.div`
  flex: 1;
  padding: 20px 0;
  background-color: #e8f5f3;
  padding: 30px 25px;
  margin: 0 -25px;

  @media (max-width: 768px) {
    padding: 25px 20px;
    margin: 0 -20px;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
    margin: 0 -15px;
    border-radius: 15px;
  }
`;

const ContentTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 2.2rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 25px;
  line-height: 1.3;

  @media (max-width: 1024px) {
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 20px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin-bottom: 15px;
    text-align: center;
  }
`;

const ContentText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  color: #555;
  line-height: 1.8;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 15px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 12px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const Signature = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  color: var(--primary-color);
  font-weight: 600;
  font-style: italic;
  margin-top: 20px;
  text-align: right;
`;

const ImageContainer = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1024px) {
    gap: 15px;
  }

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const LastSectionImageContainer = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1024px) {
    gap: 15px;
  }

  @media (max-width: 768px) {
    display: none;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const LastSectionTextContent = styled.div`
  flex: 1;
  padding: 20px 0;
  background-color: #e8f5f3;
  padding: 30px 25px;
  margin: 0 -25px;

  @media (max-width: 768px) {
    padding: 25px 20px;
    margin: 0 -20px;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
    margin: 0 -15px;
    border-radius: 15px;
  }
`;

const AboutUsText = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  padding: 30px 20px;
  background-color: #e8f5f3;
  border-radius: 15px;
  margin-top: -60px;
`;

const AboutUsDescription = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 1.1rem;
  color: #555;
  line-height: 1.8;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

const MobileGallery = styled.div`
  display: none;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin: 40px 0;

  @media (max-width: 768px) {
    display: grid;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const MobileImageWrapper = styled.div`
  position: relative;
  height: 200px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 480px) {
    height: 180px;
    border-radius: 10px;
  }
`;

const MobileSingleImage = styled.div`
  display: none;
  margin: 20px 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileImage = styled.div`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 480px) {
    height: 180px;
    border-radius: 10px;
  }
`;

const WelcomeMatImage = styled.div`
  display: none;
  margin: 30px 0;
  text-align: center;

  @media (max-width: 768px) {
    display: block;
  }

  img {
    width: 100%;
    max-width: 300px;
    height: auto;
    border-radius: 15px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }
`;

const DoorImage = styled.div`
  display: none;
  margin: 30px 0;
  text-align: center;

  @media (max-width: 768px) {
    display: block;
  }

  img {
    width: 100%;
    max-width: 300px;
    height: auto;
    border-radius: 15px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }
`;

const HouseImage = styled.div`
  display: none;
  margin: 30px 0;
  text-align: center;

  @media (max-width: 768px) {
    display: block;
  }

  img {
    width: 100%;
    max-width: 300px;
    height: auto;
    border-radius: 15px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.02);
    }
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 200px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  &:nth-child(1) {
    grid-row: span 2;
    height: 420px;

    @media (max-width: 1024px) {
      height: 350px;
    }

    @media (max-width: 768px) {
      height: 200px;
      grid-row: span 1;
    }

    @media (max-width: 480px) {
      height: 180px;
    }
  }

  @media (max-width: 768px) {
    height: 180px;
  }

  @media (max-width: 480px) {
    height: 160px;
    border-radius: 10px;
  }
`;

const Image = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${props => props.bgImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-left: 4px solid var(--primary-color);
  padding: 30px;
  margin: 40px 0;
  border-radius: 0 15px 15px 0;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 30px 0;
  }

  @media (max-width: 480px) {
    padding: 15px;
    margin: 20px 0;
    border-radius: 0 10px 10px 0;
  }
`;

const HighlightText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 1.2rem;
  color: #2c3e50;
  line-height: 1.7;
  font-weight: 500;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    line-height: 1.5;
  }
`;

const PropertiesSection = () => {
  return (
    <CompanyContainer>
      <ContentWrapper>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <SectionHeader>
            <SectionTitle>About Nova Elite Homes</SectionTitle>
          </SectionHeader>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <AboutUsText>
            <AboutUsDescription>
              At Nova Elite Homes Ltd, we specialise in providing comfortable and convenient accommodation services for working professionals and students in London. Whether you are relocating for a project, moving for studies, on a short-term assignment, or simply need a place to stay while in the city, we make the process seamless.
            </AboutUsDescription>
          </AboutUsText>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          variants={staggerContainer}
        >
          <MobileGallery>
            <motion.div variants={imageVariants}>
              <MobileImageWrapper>
                <Image bgImage="/images/airbnb1.jpg" />
              </MobileImageWrapper>
            </motion.div>
            <motion.div variants={imageVariants}>
              <MobileImageWrapper>
                <Image bgImage="/images/airbnb2.jpg" />
              </MobileImageWrapper>
            </motion.div>
            <motion.div variants={imageVariants}>
              <MobileImageWrapper>
                <Image bgImage="/images/airbnb3.jpg" />
              </MobileImageWrapper>
            </motion.div>
            <motion.div variants={imageVariants}>
              <MobileImageWrapper>
                <Image bgImage="/images/airbnb4.jpg" />
              </MobileImageWrapper>
            </motion.div>
            <motion.div variants={imageVariants}>
              <MobileImageWrapper>
                <Image bgImage="/images/airbnb5.jpg" />
              </MobileImageWrapper>
            </motion.div>
            <motion.div variants={imageVariants}>
              <MobileImageWrapper>
                <Image bgImage="/images/airbnb6.jpg" />
              </MobileImageWrapper>
            </motion.div>
          </MobileGallery>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <TextContent>
              <ContentTitle>Our Mission</ContentTitle>
              <ContentText>
                Our journey began as a small company with a simple but powerful idea: to make finding quality accommodation in London easier for professionals and students. What started with just a handful of properties and a determination to do things differently has grown into a thriving service trusted by individuals and businesses across the city.
              </ContentText>
              <ContentText>
                Our director built Nova Elite Homes Ltd from the ground up. Having worked for years in property administration, she gained extensive experience in managing homes, coordinating with clients, and understanding exactly what people need when relocating or working in a busy city like London. With that expertise, she transformed her knowledge and passion into a business dedicated to providing excellent living spaces and outstanding service.
              </ContentText>
              <ContentText>
                From the very beginning, her focus has been on more than just providing accommodation. She envisioned creating a service where professionals and students could feel at home, supported, and cared for during their time in London. That same vision still guides us today.
              </ContentText>
              <ContentText>
                We take pride in looking after every property with care and attention, ensuring our clients feel comfortable and valued. Many of our homes are thoughtfully arranged to bring people together in shared living spaces, while still offering privacy and comfort. As we continue to grow, we remain true to the standards that shaped us from the start: integrity, reliability, and excellence in everything we do.
              </ContentText>
            </TextContent>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <ImageContainer>
              <ImageWrapper>
                <Image bgImage="/images/airbnb3.jpg" />
              </ImageWrapper>
              <ImageWrapper>
                <Image bgImage="/images/airbnb6.jpg" />
              </ImageWrapper>
              <ImageWrapper>
                <Image bgImage="/images/airbnb1.jpg" />
              </ImageWrapper>
            </ImageContainer>
          </motion.div>
        </ContentSection>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <WelcomeMatImage>
            <img src="/images/Welcome mat.jpg" alt="Welcome Mat" />
          </WelcomeMatImage>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <ImageContainer>
              <ImageWrapper>
                <Image bgImage="/images/airbnb2.jpg" />
              </ImageWrapper>
              <ImageWrapper>
                <Image bgImage="/images/airbnb4.jpg" />
              </ImageWrapper>
              <ImageWrapper>
                <Image bgImage="/images/airbnb5.jpg" />
              </ImageWrapper>
            </ImageContainer>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <TextContent>
              <ContentTitle>Our Vision</ContentTitle>
              <ContentText>
                Our vision is to be the trusted choice for accommodation in London, offering living spaces that allow individuals and teams to thrive while carrying forward the determination, passion, and excellence on which Nova Elite Homes Ltd was built.
              </ContentText>
            </TextContent>
          </motion.div>
        </ContentSection>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <DoorImage>
            <img src="/images/door.jpg" alt="Door" />
          </DoorImage>
        </motion.div>

        <ContentSection>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInLeft}
          >
            <TextContent>
              <ContentTitle>Why Choose Us?</ContentTitle>
            <ContentText>
              Choosing where to stay in London can be overwhelming, but with Nova Elite Homes Ltd, you can be confident that your accommodation needs are in safe hands. Here's why so many professionals, students, and companies choose us:
            </ContentText>
            <ContentText>
              <strong>Tailored Services:</strong> We understand that every client's needs are unique. Whether you're a student looking for a comfortable place near your university or a company seeking accommodation for staff, we adapt our services to suit you.
            </ContentText>
            <ContentText>
              <strong>Prime London Locations:</strong> Our growing selection of properties is located across London, giving you access to well connected, convenient, and safe areas of the city.
            </ContentText>
            <ContentText>
              <strong>Shared Living with Comfort:</strong> Our properties are designed to provide a balance between community and privacy, giving clients the chance to enjoy affordable, practical accommodation while still feeling at home.
            </ContentText>
            <ContentText>
              <strong>Quality and Care:</strong> We treat every property as if it were our own. From upkeep to comfort, we make sure each space is ready for you to feel at home from the moment you move in.
            </ContentText>
            <ContentText>
              <strong>Professional Support:</strong> Our friendly and experienced team is always on hand to assist you before, during, and after your stay. We believe in building lasting relationships, not just short-term bookings.
            </ContentText>
            <ContentText>
              <strong>Flexible Options:</strong> Whether you need short-term or long-term accommodation, we provide flexible arrangements to match your schedule and requirements.
            </ContentText>
            <ContentText>
              <strong>Trusted by Businesses and Individuals:</strong> Companies rely on us to house their staff, and individuals trust us with one of their most important needs: a home while in London.
            </ContentText>
            <ContentText>
              With Nova Elite Homes Ltd, accommodation isn't just about a place to stay. It's about peace of mind, reliability, and a service you can count on.
            </ContentText>
            </TextContent>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.3 }}
            variants={slideInRight}
          >
            <ImageContainer>
            <ImageWrapper>
              <Image bgImage="/images/airbnb6.jpg" />
            </ImageWrapper>
            <ImageWrapper>
              <Image bgImage="/images/airbnb3.jpg" />
            </ImageWrapper>
            <ImageWrapper>
              <Image bgImage="/images/airbnb2.jpg" />
            </ImageWrapper>
            </ImageContainer>
          </motion.div>
        </ContentSection>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeInUp}
        >
          <HouseImage>
            <img src="/images/house.jpg" alt="House" />
          </HouseImage>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={slideInLeft}
        >
          <LastSectionImageContainer>
            <ImageWrapper>
              <Image bgImage="/images/airbnb1.jpg" />
            </ImageWrapper>
            <ImageWrapper>
              <Image bgImage="/images/airbnb4.jpg" />
            </ImageWrapper>
            <ImageWrapper>
              <Image bgImage="/images/airbnb5.jpg" />
            </ImageWrapper>
          </LastSectionImageContainer>
        </motion.div>
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={slideInRight}
        >
          <LastSectionTextContent>
            <ContentTitle>Our Commitment to Excellence</ContentTitle>
            <ContentText>
              Excellence is not just a goal for us, it's our standard operating procedure. We have built our reputation on consistently delivering results that exceed expectations, whether it's achieving higher rental rates, reducing maintenance costs, or creating exceptional living experiences for tenants.
            </ContentText>
            <ContentText>
              Our commitment to excellence extends to every aspect of our operations, from the initial property assessment and marketing strategy to ongoing maintenance and tenant relations. We continuously invest in staff training, technology upgrades, and process improvements to ensure we remain at the forefront of industry best practices.
            </ContentText>
            <ContentText>
              We measure our success not just by financial metrics, but by the satisfaction of our clients, the happiness of our tenants, and the positive impact we have on the communities we serve. This holistic approach to excellence has earned us recognition as a trusted partner in the property management industry and a preferred choice for discerning property owners.
            </ContentText>
          </LastSectionTextContent>
        </motion.div>
      </ContentWrapper>
    </CompanyContainer>
  );
};

export default PropertiesSection;
